import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

// Bootstrap Components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

// Context
import { useProducts } from "../context/ProductsContext";

// Custom components
import ProductCard from "../components/ProductCard/ProductCard";
import PriceRangeSlider from "../components/PriceRangeSlider/PriceRangeSlider";

// API
import { getConditionProducts } from "../api/product";
import { getCondition } from "../api/genre";

// Util
import { toggleClass } from "../utils/toggleClass";

// Assets
import FilterIcon from "../assets/images/filter-icon.svg";

// Custom styles
import "../assets/scss/pages/condition.scss";

// Constants
const INITIAL_FILTER_STATE = {
  calibre: {},
  tipoCorrea: {},
  genero: {},
  marca: {},
  tipo: {},
  caratula: {},
  carcasa: {},
};

const FILTER_TYPES = {
  CALIBRE: "calibre",
  TIPO_CORREA: "tipoCorrea",
  GENERO: "genero",
  MARCA: "marca",
  TIPO: "tipo",
  CARATULA: "caratula",
  CARCASA: "carcasa",
};

function Condition() {
  const { slug } = useParams();

  // Main data states
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [condition, setCondition] = useState(null);

  // Loading states - consolidated
  const [loadingStates, setLoadingStates] = useState({
    products: true,
    condition: true,
  });

  // Filter states
  const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState(null);
  const [filterResetTrigger, setFilterResetTrigger] = useState(0);

  // Mobile filter state
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Add state for dropdown toggles
  const [openDropdowns, setOpenDropdowns] = useState({
    correa: false,
    genero: false,
    marca: false,
    tipo: false,
    calibre: false,
    caratula: false,
    carcasa: false,
  });

  // Toggle dropdown function
  const toggleDropdown = useCallback((dropdownName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  }, []);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Mobile filter toggle handler
  const handleMobileFilterToggle = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  // Loading helper
  const updateLoadingState = useCallback((key, value) => {
    setLoadingStates((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Extract filter options from products data
  const getUniqueWatchBands = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueBands = [...new Set(allProducts.map(product => product.tipoCorrea?.nombre).filter(Boolean))];
    return uniqueBands.sort();
  };

  const getUniqueWatchGenres = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueGenres = [...new Set(allProducts.map(product => product.genero?.nombre).filter(Boolean))];
    return uniqueGenres.sort();
  };

  const getUniqueBrands = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueBrands = [...new Set(allProducts.map(product => product.marca?.nombre).filter(Boolean))];
    return uniqueBrands.sort();
  };

  const getUniqueWatchTypes = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueTypes = [...new Set(allProducts.map(product => product.tipo?.nombre).filter(Boolean))];
    return uniqueTypes.sort();
  };

  const getUniqueWatchCalibers = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueCalibers = [...new Set(allProducts.map(product => product.calibre?.nombre).filter(Boolean))];
    return uniqueCalibers.sort();
  };

  const getUniqueCaratulas = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueCaratulas = [...new Set(allProducts.map(product => product.colorCaratula?.color).filter(Boolean))];
    return uniqueCaratulas.sort();
  };

  const getUniqueCarcasas = () => {
    if (!allProducts || allProducts.length === 0) return [];
    const uniqueCarcasas = [...new Set(allProducts.map(product => product.materialCarcasa?.material).filter(Boolean))];
    return uniqueCarcasas.sort();
  };

  // Check if all critical data is loaded
  const isDataLoaded = useMemo(() => {
    return !loadingStates.products;
  }, [loadingStates.products]);

  // Fetch main data
  useEffect(() => {
    const fetchMainData = async () => {
      if (!slug) return;

      try {
        const [productsRes, conditionRes] = await Promise.all([
          getConditionProducts(slug, 1, 1000),
          getCondition(slug),
        ]);

        if (productsRes?.docs) {
          setAllProducts(productsRes.docs);
          setProducts(productsRes.docs);
        }
        updateLoadingState("products", false);

        if (conditionRes?.docs?.[0]) {
          setCondition(conditionRes.docs[0]);
        }
        updateLoadingState("condition", false);
      } catch (error) {
        console.error("Error fetching main data:", error);
        updateLoadingState("products", false);
        updateLoadingState("condition", false);
      }
    };

    fetchMainData();
  }, [slug, updateLoadingState]);

  // Filter toggle handler
  const handleFilterToggle = useCallback((filterName, filterType) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterType]: {
        ...prev[filterType],
        [filterName]: !prev[filterType][filterName],
      },
    }));

    const categoryKey = filterName.toLowerCase();
    setSelectedCategories((prev) =>
      prev.includes(categoryKey)
        ? prev.filter((cat) => cat !== categoryKey)
        : [...prev, categoryKey]
    );
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setActiveFilters(INITIAL_FILTER_STATE);
    setSelectedCategories([]);
    setFilterResetTrigger(prev => prev + 1);

    // Remove active class from DOM elements
    const activeElements = document.querySelectorAll(
      ".filters__filter__dropdown-menu__item__name.active"
    );
    activeElements.forEach((el) => el.classList.remove("active"));
  }, []);

  // Filter products based on active filters
  const filteredProducts = useMemo(() => {
    let filtered = allProducts;

    // Filter by categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product) => {
        return Object.entries(activeFilters).every(([filterType, filters]) => {
          const activeFiltersForType = Object.entries(filters)
            .filter(([_, isActive]) => isActive)
            .map(([filterName]) => filterName);

          if (activeFiltersForType.length === 0) return true;

          // Handle special cases for caratula and carcasa
          if (filterType === 'caratula') {
            return activeFiltersForType.includes(product.colorCaratula?.color);
          }
          if (filterType === 'carcasa') {
            return activeFiltersForType.includes(product.materialCarcasa?.material);
          }

          return activeFiltersForType.includes(product[filterType]?.nombre);
        });
      });
    }

    // Filter by price range (only if we have enough products to show the slider)
    if (priceRange && allProducts && allProducts.length >= 5) {
      filtered = filtered.filter((product) => {
        const price = parseFloat(product.precio || product.price || 0);
        return price >= priceRange.min && price <= priceRange.max;
      });
    }

    return filtered;
  }, [products, activeFilters, selectedCategories, priceRange]);

  // Render filter dropdown
  const renderFilterDropdown = useCallback(
    (title, items, filterType, dropdownKey, loading = false) => {
      if (loading) return null;

      return (
        <div className={`filters__filter filters__filter--dropdown ${openDropdowns[dropdownKey] ? 'open' : ''}`}>
          <div className="filters__filter__content">
            <p
              className="filters__filter__name"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDropdown(dropdownKey);
              }}
              style={{ cursor: 'pointer' }}
            >
              {title}
            </p>
            <div className={`filters__filter__dropdown-menu ${openDropdowns[dropdownKey] ? 'open' : ''}`}>
              <ul className="filters__filter__dropdown-menu__item-container">
                {items.map((item, index) => (
                  <li
                    key={`${filterType}-${index}`}
                    className="filters__filter__dropdown-menu__item filter__item"
                    data-filter={item.toLowerCase()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFilterToggle(item, filterType);

                      // Close mobile filter menu
                      const filtersDropdownMenu = document.querySelector(
                        ".filters__filter-container"
                      );
                      if (filtersDropdownMenu) {
                        filtersDropdownMenu.classList.remove("show");
                      }

                      // Close mobile filter menu after selection
                      if (isMobile && isMobileFilterOpen) {
                        setTimeout(() => {
                          setIsMobileFilterOpen(false);
                        }, 300);
                      }

                      e.target.classList.toggle("active");
                    }}
                  >
                    <p className="filters__filter__dropdown-menu__item__name text-capitalize">
                      {item}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    },
    [handleFilterToggle, openDropdowns, toggleDropdown]
  );

  // Loading state
  if (!isDataLoaded) {
    return (
      <section className="brand-catalogue">
        <Container>
          <p className="py-4">Cargando...</p>
        </Container>
      </section>
    );
  }

  return (
    <section className="condition__catalogue">
      {/* Header */}
      <div
        className="products__genre__header"
        style={{
          backgroundImage: condition?.portada?.url
            ? `linear-gradient(rgba(0, 0, 0, 0.53), rgba(0, 0, 0, 0.275)), url(${condition.portada.url})`
            : undefined,
        }}
      >
        <h3 className="text-capitalize">{condition?.nombre}</h3>
      </div>

      {/* Main Content */}
      <div className="condition__catalogue__products">
        <Container fluid>
          <Row>
            {/* Filters */}
            <div className="condition__catalogue__filters col-12 col-lg-3">
              <div className="filters__container">
                <div
                  className="filters__header"
                  style={{ cursor: isMobile ? "pointer" : "default" }}
                  onClick={isMobile ? handleMobileFilterToggle : undefined}
                >
                  <img
                    src={FilterIcon}
                    alt="Filtros"
                    className="filters__icon"
                  />
                  <p className="filters__title">Filtros</p>
                  {isMobile && (
                    <span
                      className="d-block d-lg-none ms-3"
                      style={{ fontSize: "1.1rem", fontWeight: "bolder" }}
                    >
                      {isMobileFilterOpen
                        ? "Toca para ocultar"
                        : "Toca para expandir filtros"}
                    </span>
                  )}
                </div>

                <div className={`filters__filter-container ${isMobile ? (isMobileFilterOpen ? "show" : "") : ""}`}>
                  {/* Clear Filters */}
                  <div className="filters__filter filters__filter--clear filter__item">
                    <div className="filters__filter__content">
                      <p
                        className="filters__filter__name filters__filter__name--small"
                        onClick={clearAllFilters}
                      >
                        Limpiar filtros <i className="far fa-times"></i>
                      </p>
                    </div>
                  </div>

                  {/* Price Range Slider - Only show if 5+ products */}
                  {allProducts && allProducts.length >= 5 && (
                    <PriceRangeSlider
                      products={allProducts}
                      onPriceChange={setPriceRange}
                      resetTrigger={filterResetTrigger}
                    />
                  )}

                  {/* Filter Dropdowns */}
                  {renderFilterDropdown(
                    "Correa",
                    getUniqueWatchBands(),
                    FILTER_TYPES.TIPO_CORREA,
                    "correa",
                    false
                  )}
                  {renderFilterDropdown(
                    "Género",
                    getUniqueWatchGenres(),
                    FILTER_TYPES.GENERO,
                    "genero",
                    false
                  )}
                  {renderFilterDropdown(
                    "Marca",
                    getUniqueBrands(),
                    FILTER_TYPES.MARCA,
                    "marca",
                    false
                  )}
                  {renderFilterDropdown(
                    "Tipo",
                    getUniqueWatchTypes(),
                    FILTER_TYPES.TIPO,
                    "tipo",
                    false
                  )}
                  {renderFilterDropdown(
                    "Calibre",
                    getUniqueWatchCalibers(),
                    FILTER_TYPES.CALIBRE,
                    "calibre",
                    false
                  )}
                  {renderFilterDropdown(
                    "Color de Carátula",
                    getUniqueCaratulas(),
                    FILTER_TYPES.CARATULA,
                    "caratula",
                    false
                  )}
                  {renderFilterDropdown(
                    "Material de Carcasa",
                    getUniqueCarcasas(),
                    FILTER_TYPES.CARCASA,
                    "carcasa",
                    false
                  )}
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="col-12 col-lg-9">
              <Row>
                {/* Show products based on filters and price range */}
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product, index) => (
                    <ProductCard
                      key={`product-${product.id || index}`}
                      product={product}
                      loading={false}
                    />
                  ))
                ) : (
                  <p className="py-3">
                    {selectedCategories.length > 0 || (priceRange && products && products.length >= 5 && (priceRange.min > 0 || priceRange.max < 100000))
                      ? "No se encontraron relojes con los filtros seleccionados."
                      : "No hay productos"}
                  </p>
                )}
              </Row>
            </div>
          </Row>
        </Container>
      </div>
    </section>
  );
}

export default Condition;
